'use client';

import React, { useState, useEffect } from 'react';
import { X, Plus, Clock, CheckCircle, Loader2, Search, Tag as TagIcon } from 'lucide-react';
import { Tag as TagType } from '@/types/tags';
import { tagService } from '@/lib/tag-service';
import { tagPermissionService } from '@/lib/tag-permission-service';
import { formatPublishedDate } from '@/utils/date-formatting';
import { useToast } from '../ui/Toast';

interface TagRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  assignedTags: string[]; // Array of assigned tag slugs
  requestedTags: string[]; // Array of requested tag slugs
  tagRequestedAt?: { [tagSlug: string]: Date }; // Timestamps for when tags were requested
  onRequestSubmitted: () => void;
}

export function TagRequestModal({
  isOpen,
  onClose,
  userId,
  assignedTags,
  requestedTags,
  tagRequestedAt = {},
  onRequestSubmitted,
}: TagRequestModalProps) {
  const [allTags, setAllTags] = useState<TagType[]>([]);
  const [selectedTagSlugs, setSelectedTagSlugs] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (isOpen) {
      loadTags();
    }
  }, [isOpen]);

  const loadTags = async () => {
    setLoading(true);
    try {
      const tags = await tagService.getAllTags({ isActive: true });
      setAllTags(tags);
    } catch (error) {
      console.error('Error loading tags:', error);
      toast.error('Failed to load tags', 'Please try again later');
    } finally {
      setLoading(false);
    }
  };

  // Filter tags by search term
  const filteredTags = allTags.filter(tag =>
    tag.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tag.slug.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Categorize tags
  const assignedTagsList = filteredTags.filter(tag => assignedTags.includes(tag.slug));
  const pendingTagsList = filteredTags.filter(tag => requestedTags.includes(tag.slug));
  const availableTagsList = filteredTags.filter(
    tag => !assignedTags.includes(tag.slug) && !requestedTags.includes(tag.slug)
  );

  const handleToggleTag = (tagSlug: string) => {
    setSelectedTagSlugs(prev =>
      prev.includes(tagSlug)
        ? prev.filter(slug => slug !== tagSlug)
        : [...prev, tagSlug]
    );
  };

  const handleSubmitRequests = async () => {
    if (selectedTagSlugs.length === 0) return;

    setSubmitting(true);
    try {
      // Submit each tag request
      const promises = selectedTagSlugs.map(slug =>
        tagPermissionService.requestTagAccess(userId, slug)
      );
      await Promise.all(promises);

      toast.success(
        'Tag requests submitted!',
        `Requested access to ${selectedTagSlugs.length} tag${selectedTagSlugs.length !== 1 ? 's' : ''}. Admins will review your request.`
      );

      setSelectedTagSlugs([]);
      onRequestSubmitted();
      onClose();
    } catch (error: any) {
      console.error('Error submitting tag requests:', error);
      toast.error(
        'Failed to submit requests',
        error.message || 'Please try again later'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-80 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-brand-dark-lighter rounded-2xl shadow-xl max-w-4xl w-full p-8">
          <div className="flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600 dark:text-blue-400" />
            <span className="ml-3 text-gray-600 dark:text-gray-300">Loading tags...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-80 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white dark:bg-brand-dark-lighter rounded-2xl shadow-xl max-w-4xl w-full my-8">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-neutral-800">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">My Tags</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Request access to tags to write about new topics
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Assigned Tags Section */}
          {assignedTagsList.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                <CheckCircle className="w-4 h-4 mr-2 text-green-600 dark:text-green-400" />
                Assigned Tags ({assignedTagsList.length})
              </h3>
              <div className="flex flex-wrap gap-2">
                {assignedTagsList.map(tag => (
                  <div
                    key={tag.id}
                    className="inline-flex items-center px-3 py-1.5 bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700 text-green-800 dark:text-green-300 text-sm font-medium rounded-lg"
                  >
                    <CheckCircle className="w-3.5 h-3.5 mr-1.5" />
                    {tag.name}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pending Requests Section */}
          {pendingTagsList.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                <Clock className="w-4 h-4 mr-2 text-yellow-600 dark:text-yellow-400" />
                Pending Requests ({pendingTagsList.length})
              </h3>
              <div className="flex flex-wrap gap-2">
                {pendingTagsList.map(tag => (
                  <div
                    key={tag.id}
                    className="inline-flex items-center px-3 py-1.5 bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700 text-yellow-800 dark:text-yellow-300 text-sm font-medium rounded-lg"
                  >
                    <Clock className="w-3.5 h-3.5 mr-1.5" />
                    <span className="mr-2">{tag.name}</span>
                    {tagRequestedAt[tag.slug] && (
                      <span className="text-xs opacity-75">
                        {formatPublishedDate(tagRequestedAt[tag.slug])}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty State for New Writers */}
          {assignedTagsList.length === 0 && pendingTagsList.length === 0 && (
            <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 rounded-r-lg p-4">
              <div className="flex items-start gap-3">
                <TagIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                    Welcome! You don't have any assigned tags yet.
                  </p>
                  <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                    These tags determine what topics you can write about. Request tags below to get started.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Search Bar */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search tags by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-neutral-900 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
              />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Showing {filteredTags.length} of {allTags.length} tags
            </p>
          </div>

          {/* Available Tags Section */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center justify-between">
              <span className="flex items-center">
                <Plus className="w-4 h-4 mr-2 text-gray-600 dark:text-gray-400" />
                Request Tag Access ({availableTagsList.length})
              </span>
              {selectedTagSlugs.length > 0 && (
                <span className="text-xs text-blue-600 dark:text-blue-400">
                  {selectedTagSlugs.length} tag{selectedTagSlugs.length !== 1 ? 's' : ''} selected
                </span>
              )}
            </h3>

            {availableTagsList.length === 0 ? (
              <div className="text-center py-8">
                <TagIcon className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-3" />
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  {searchTerm
                    ? 'No tags match your search'
                    : 'You have access to all available tags!'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-96 overflow-y-auto">
                {availableTagsList.map(tag => (
                  <button
                    key={tag.id}
                    onClick={() => handleToggleTag(tag.slug)}
                    className={`flex items-center px-3 py-2 border rounded-lg text-sm font-medium transition-all text-left ${selectedTagSlugs.includes(tag.slug)
                      ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-500 dark:border-blue-400 text-blue-800 dark:text-blue-200'
                      : 'bg-gray-50 dark:bg-slate-700/50 border-gray-300 dark:border-neutral-900 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-600'
                      }`}
                  >
                    <div
                      className={`w-4 h-4 mr-2 rounded border flex items-center justify-center flex-shrink-0 ${selectedTagSlugs.includes(tag.slug)
                        ? 'bg-blue-600 border-blue-600'
                        : 'bg-white dark:bg-brand-dark-lighter border-gray-300 dark:border-slate-500'
                        }`}
                    >
                      {selectedTagSlugs.includes(tag.slug) && (
                        <CheckCircle className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <span className="truncate">{tag.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info Box */}
          <div className="mt-6 bg-gray-50 dark:bg-slate-700/50 rounded-lg p-4 border border-gray-200 dark:border-neutral-900">
            <p className="text-xs text-gray-600 dark:text-gray-400">
              <strong>How it works:</strong> Select the tags you want to write about and click "Request Access".
              Your request will be reviewed by editors and admins. Once approved, you'll be able to create articles
              using those tags.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-neutral-800 bg-gray-50 dark:bg-brand-dark/50">
          <button
            onClick={onClose}
            className="px-6 py-2.5 border-2 border-gray-300 dark:border-neutral-900 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
          >
            Close
          </button>
          {selectedTagSlugs.length > 0 && (
            <button
              onClick={handleSubmitRequests}
              disabled={submitting}
              className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl shadow-lg hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Request Access ({selectedTagSlugs.length})
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

